import axios from 'axios'
import { tokenStorage } from 'core/network/tokenStorage'
import { User, UserRole } from '../../domain/entities/User'
import { AuthRepository } from '../../domain/repositories/AuthRepository'
import { authApiDatasource, MeApiResponse, SelfApiResponse } from '../datasources/AuthApiDatasource'

// This admin portal is for internal staff only; external roles authenticate through the mobile app.
const PORTAL_RESTRICTED_ROLES: UserRole[] = ['fpo', 'vlcc', 'retailer']

const deriveNameFromEmail = (email?: string): string => {
  if (!email || typeof email !== 'string') return ''
  const username = email.split('@')[0] || ''
  return (
    username
      .split(/[._]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || email
  )
}

// /api/v1/me is the lightweight source for role + permissions (parsed straight from the JWT).
// /api/v1/users/self is the source for the rest of the profile (name, phone, status).
const toUser = (me: MeApiResponse, self: SelfApiResponse): User => {
  const email = self?.email || me?.email || ''
  const derivedName = deriveNameFromEmail(email)
  const name = self?.name || derivedName || email || 'Admin User'

  return {
    id: self?.id || me?.user_id || '',
    email,
    role: (me?.role || self?.role || 'admin') as UserRole,
    permissions: me?.permissions || [],
    name,
    phone: self?.phone || '',
    status: self?.status || 'active',
  }
}

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

export class AuthRepositoryImpl implements AuthRepository {
  async login(email: string, password: string): Promise<User> {
    let tokens
    try {
      tokens = await authApiDatasource.login(email, password)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Invalid credentials. Please try again.'))
    }

    tokenStorage.save(tokens.access_token, tokens.refresh_token, tokens.expires_in)

    try {
      const [me, self] = await Promise.all([authApiDatasource.getMe(), authApiDatasource.getSelf()])

      if (PORTAL_RESTRICTED_ROLES.includes(me.role as UserRole)) {
        throw new Error('Access Denied: This portal is restricted to internal staff only.')
      }

      return toUser(me, self)
    } catch (err) {
      tokenStorage.clear()
      throw err instanceof Error ? err : new Error(extractErrorMessage(err, 'Failed to load user profile.'))
    }
  }

  async logout(): Promise<void> {
    tokenStorage.clear()
  }

  async getCurrentUser(): Promise<User | null> {
    const accessToken = tokenStorage.getAccessToken()
    const refreshToken = tokenStorage.getRefreshToken()
    if (!accessToken && !refreshToken) {
      tokenStorage.clear()
      return null
    }

    try {
      const [me, self] = await Promise.all([authApiDatasource.getMe(), authApiDatasource.getSelf()])
      return toUser(me, self)
    } catch {
      tokenStorage.clear()
      return null
    }
  }
}
